import { createApi } from '@reduxjs/toolkit/query/react';
import { fronteggBaseQuery } from '../../fronteggBaseQuery';
import {
  type FronteggPaginationWrapper,
  type ISearchUserQueryParamsV2,
  type IGetUsersV2Response,
  SortByEnum,
  PaginationOrderEnum,
  ResendUserInvitationEmailPayload,
} from '../types';
import { extractOffsetFromLink } from '../../../utils/extractOffsetFromLink';

export const fronteggUsersApi = createApi({
  baseQuery: fronteggBaseQuery(),
  reducerPath: 'fronteggUsers',
  tagTypes: ['TenantUser'],
  endpoints: (builder) => ({
    getUsers: builder.infiniteQuery<
      FronteggPaginationWrapper<IGetUsersV2Response>,
      ISearchUserQueryParamsV2 | void,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage) =>
          extractOffsetFromLink(lastPage._links?.next),
      },
      query: ({ queryArg, pageParam }) => ({
        url: 'identity/resources/users/v2',
        method: 'GET',
        params: {
          ...(queryArg ?? {}),
          _sortBy: SortByEnum.name,
          _order: PaginationOrderEnum.ASC,
          _offset: pageParam,
          _limit: 200,
        },
      }),
      providesTags: (result) => {
        if (result?.pages) {
          return [
            ...result.pages.flatMap((page) =>
              page.items.map((item) => ({
                type: 'TenantUser' as const,
                id: item.id,
              })),
            ),
            { type: 'TenantUser' as const, id: 'LIST' },
          ];
        }
        return [{ type: 'TenantUser' as const, id: 'LIST' }];
      },
    }),
    resendInvite: builder.mutation<void, ResendUserInvitationEmailPayload>({
      query: (payload) => ({
        url: 'identity/resources/users/v1/activate/reset',
        method: 'POST',
        body: payload,
      }),
    }),
  }),
});

export const {
  endpoints: { getUsers, resendInvite },
  useGetUsersInfiniteQuery,
  useResendInviteMutation,
} = fronteggUsersApi;
